<template>
    <div class="relative">
        <div
            v-if="isLoading"
            class="w-full h-full absolute top-0 left-0 bg-white opacity-50 z-10 flex items-center justify-center"
        >
            <Icon
                name="loading-spinner"
                class="w-8 h-8 text-gray-700 animate-spin"
            />
        </div>
        <div
            v-if="currentlySelectedMonth"
            class="p-2 bg-white rounded-lg2 border border-gray-200 space-y-4"
        >
            <div
                class="text-center text-lg font-semibold text-dark flex items-center justify-between select-none"
            >
                <div
                    @click="handleMoveToPreviousMonth()"
                    class="p-3 hover:bg-gray-100 rounded-lg2 cursor-pointer"
                    :class="{ invisible: isPreviousMonthButtonDisabled }"
                >
                    <Icon
                        name="angle-left"
                        class="w-4 h-4"
                    />
                </div>
                <div class="cursor-default">
                    {{
                        localizer.translate(
                            currentlySelectedMonth.monthTextToken,
                        )
                    }}
                    {{ currentlySelectedMonth.year }}
                </div>
                <div
                    @click="handleMoveToNextMonth()"
                    class="p-3 hover:bg-gray-100 rounded-lg2 cursor-pointer"
                    :class="{ invisible: isNextMonthButtonDisabled }"
                >
                    <Icon
                        name="angle-right"
                        class="w-4 h-4"
                    />
                </div>
            </div>
            <div class="text-center">
                <div
                    class="grid grid-cols-7 text-xs text-gray-500 cursor-default"
                >
                    <div>
                        {{ localizer.translate(TextTokenEnum.BookingForm_SUN) }}
                    </div>
                    <div>
                        {{ localizer.translate(TextTokenEnum.BookingForm_MON) }}
                    </div>
                    <div>
                        {{ localizer.translate(TextTokenEnum.BookingForm_TUE) }}
                    </div>
                    <div>
                        {{ localizer.translate(TextTokenEnum.BookingForm_WED) }}
                    </div>
                    <div>
                        {{ localizer.translate(TextTokenEnum.BookingForm_THU) }}
                    </div>
                    <div>
                        {{ localizer.translate(TextTokenEnum.BookingForm_FRI) }}
                    </div>
                    <div>
                        {{ localizer.translate(TextTokenEnum.BookingForm_SAT) }}
                    </div>
                </div>
                <div
                    class="isolate mt-2 grid grid-cols-7 gap-1.5 rounded-lg2 text-sm"
                >
                    <template
                        v-for="(week, index1) in currentlySelectedMonth.weeks"
                        :key="index1"
                    >
                        <template
                            v-for="(dayData, index2) in week.days"
                            :key="index2"
                        >
                            <button
                                type="button"
                                :class="getCalendarDayButtonColor(dayData)"
                                class="rounded-lg2 py-2 focus:z-10 font-bold ring-inset"
                                @click="handleSelectedDate(dayData)"
                            >
                                <time
                                    :datetime="dayData.date"
                                    class="mx-auto flex h-7 w-7 items-center justify-center"
                                >
                                    {{ dayData.dayOfMonthNumber }}
                                </time>
                            </button>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, type PropType } from 'vue'
    import Icon from '@/Shared/Component/Icon.vue'
    import {
        type CalendarDayType,
        type CalendarMonthType,
    } from '@/Shared/AutoGenerated/Main'
    import type LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'

    const emit = defineEmits<{
        onDateSelectionChange: [{ date: string | null }]
        onMonthSelectionChange: []
    }>()

    const props = defineProps({
        months: {
            type: Array as PropType<CalendarMonthType[]>,
            required: true,
        },
        selectedDate: {
            type: String,
            default: null,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
        useBookingFormThemeCssVariables: {
            type: Boolean,
            default: false,
        },
        localizer: {
            type: Object as PropType<LocalizerService>,
            required: true,
        },
    })

    /**
     * Calendar day button color
     */
    const getCalendarDayButtonColor = (dayData: CalendarDayType): string => {
        // Day from previous or next month, should be invisible
        if (dayData.isVisible === false) {
            return getInvisibleButtonCssClasses()
        }

        // Day has no timeslot available, should be grayed out
        if (dayData.isAvailable === false) {
            return getDisabledButtonCssClasses()
        }

        const isSelected = props.selectedDate === dayData.date

        return props.useBookingFormThemeCssVariables
            ? getThemedButtonCssClasses(isSelected)
            : getDefaultButtonCssClasses(isSelected)
    }

    const getInvisibleButtonCssClasses = (): string => {
        return [
            'invisible',

            'border',
            'border-transparent',

            'ring-2',
            'ring-transparent',
        ].join(' ')
    }

    const getDisabledButtonCssClasses = (): string => {
        return [
            'text-gray-300',

            'bg-white',

            'cursor-default',

            'border',
            'border-transparent',

            'ring-2',
            'ring-transparent',
        ].join(' ')
    }

    const getDefaultButtonCssClasses = (isSelected: boolean): string => {
        if (isSelected) {
            return [
                'bg-primary-shade-ligth',

                'border',
                'border-transparent',

                'ring-2',
                'ring-primary',

                'text-dark',

                'cursor-default',
            ].join(' ')
        }

        return [
            'bg-white',

            'border',
            'border-gray-200',

            'ring-2',
            'ring-transparent',
            'hover:ring-primary',

            'text-gray-600',

            'cursor-pointer',
        ].join(' ')
    }

    const getThemedButtonCssClasses = (isSelected: boolean): string => {
        if (isSelected && props.useBookingFormThemeCssVariables) {
            return [
                'bg-booking-form-theme-main-background-shade',

                'border',
                'border-transparent',

                'ring-2',
                'ring-booking-form-theme-main-background-color',

                'text-booking-form-theme-main-text-color',

                'cursor-default',
            ].join(' ')
        }

        return [
            'bg-white',

            'border',
            'border-gray-200',
            'hover:border-transparent',

            'ring-2',
            'ring-transparent',
            'hover:ring-booking-form-theme-main-background-color',

            'text-booking-form-theme-main-text-color',

            'cursor-pointer',
        ].join(' ')
    }

    /**
     * Date selection
     */
    const handleSelectedDate = (dayData: CalendarDayType) => {
        if (!dayData.isVisible || !dayData.isAvailable) {
            return
        }

        emit('onDateSelectionChange', { date: dayData.date })
    }

    /**
     * Month navigation
     */
    const currentlySelectedMonthIndex = ref<number>(0)

    const currentlySelectedMonth = computed<CalendarMonthType>(() => {
        return props.months[currentlySelectedMonthIndex.value] ?? null
    })

    const isPreviousMonthButtonDisabled = computed(() => {
        return currentlySelectedMonthIndex.value <= 0
    })

    const isNextMonthButtonDisabled = computed(() => {
        return currentlySelectedMonthIndex.value >= props.months.length - 1
    })

    const handleMoveToPreviousMonth = () => {
        if (isPreviousMonthButtonDisabled.value) {
            return
        }

        currentlySelectedMonthIndex.value =
            currentlySelectedMonthIndex.value - 1

        emit('onMonthSelectionChange')
    }

    const handleMoveToNextMonth = () => {
        if (isNextMonthButtonDisabled.value) {
            return
        }

        currentlySelectedMonthIndex.value =
            currentlySelectedMonthIndex.value + 1

        emit('onMonthSelectionChange')
    }
</script>
